import { CurrencySettings, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { IncludeExcludeList } from "@mcleod/dispatch/src/planning/PlanningProfileFilter";
import { PayablesLookup } from "../OrderPayables";
import { getPayrollControlValue } from "@mcleod/general";

export class DriverSettlementFilter {
    private filterRow: ModelRow;
    public payablesLookupMap: PayablesLookup;
    private includeLists: IncludeExcludeList[] = [];
    private startDate: Date;
    private cutOffDate: Date;

    constructor(filterRow: ModelRow, cutoffDate?: Date) {
        this.filterRow = filterRow;
        this.addIncludeExcludeList("payee_ids", "payee_id");
        this.addIncludeExcludeList("bo_reason_code", "back_office_reason_code");
        this.addIncludeExcludeList("departments", "department");
        this.addIncludeExcludeList("payment_method", "payment_method");
        this.addIncludeExcludeList("pay_rate_method", "pay_method");
        if (!filterRow.isNull("start_date"))
            this.startDate = DateUtil.parseDateWithKeywords(filterRow.get("start_date"), true, false);
        if (cutoffDate) {
            this.cutOffDate = cutoffDate;
        }
        else if (!filterRow.isNull("cutoff_date")) {
            this.cutOffDate = DateUtil.parseDateWithKeywords(filterRow.get("cutoff_date"), true, false);
        } else {
            // Not a fan of doing this because of duplicate logic but it works for now
            this.cutOffDate = this.getPayrollCutoffDate(filterRow);
        }
    }

    public dataPasses(data: ModelRow): boolean {
        if (data.get("ready to pay flag") === "V" || data.get("record_type") === "O") {
            return false;
        }
        if (!this.filterRow.isNull("pay_adjustment_codes")) {
            // If pay_adjustment_codes is set, we only want to include pay adjustments
            if (data.get("record_type") !== "P") {
                return false;
            } else {
                const payAdjustmentCodes: [] = this.filterRow.get("pay_adjustment_codes");
                if (payAdjustmentCodes.length > 0 && !this.findItem(payAdjustmentCodes, data.get("deduct_code_id"))) {
                    return false;
                }
            }
        }
        // Check for Company (C) or Owner Operator (O) payee types
        if (!(data.get("payee_type") === "C" || data.get("payee_type") === "O")) {
            return false;
        }
        for (const list of this.includeLists) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.dateTimeMatches(data)) {
            return false;
        }
        if (!this.payeeStatusMatches(data)) {
            return false;
        }
        if (!this.payeeTypeMatches(data)) {
            return false;
        }
        if (!this.payFrequencyMatches(data)) {
            return false;
        }
        if (!this.frozenPayablesMatches(data)) {
            return false;
        }
        if (!this.transactionNumMatches(data)) {
            return false;
        }
        if (!this.statusMatches(data)) {
            return false;
        }
        if (!this.currencyMatches(data)) {
            return false;
        }
        return true;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    private addIncludeExcludeList(includeExcludeListFieldName, dataFieldName?: string, includeExcludeFlagFieldName?: string) {
        this.includeLists.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName || includeExcludeListFieldName + "_exclude", false),
            values: this.tokenizeField(includeExcludeListFieldName, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    private tokenizeField(field, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return [];
        const result = this.filterRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

    private dateTimeMatches(data: ModelRow): boolean {
        let filterDate: Date = data.getDate("ok2pay_date");
        if (filterDate == null)
            return false;
        if (typeof filterDate === "string") // bummer - was hoping this would have already been a date in the ModelRow
            filterDate = DateUtil.parseDate(filterDate);
        filterDate = DateUtil.justDate(filterDate);
        return ((this.startDate == null || this.startDate <= filterDate)
            && (this.cutOffDate == null || this.cutOffDate >= filterDate));
    }

    private statusMatches(data: ModelRow): boolean {
        return data.get("ready_to_pay_flag") !== "V";
    }

    private payeeStatusMatches(data: ModelRow): boolean {
        const payeeStatus = this.filterRow.get("payee_status");
        if (payeeStatus == null) {
            return true;
        } else {
            return data.get("payee_status") === payeeStatus;
        }
    }

    private payeeTypeMatches(data: ModelRow): boolean {
        const payeeType = this.filterRow.get("payee_type");
        if (payeeType == null) {
            return true;
        } else {
            return data.get("payee_type") === payeeType;
        }
    }

    private payFrequencyMatches(data: ModelRow): boolean {
        const payFrequency = this.filterRow.get("pay_frequency");
        if (payFrequency == null) {
            return true;
        }
        return payFrequency === data.get("pay_frequency");
    }

    private frozenPayablesMatches(data: ModelRow): boolean {
        const frozenPayables = this.filterRow.get("frozen_payables");
        if (frozenPayables == null) {
            return true;
        } else if (frozenPayables === "S" && !data.isNull("freeze_by")) {
            return true;
        } else if (frozenPayables === "E" && data.isNull("freeze_by")) {
            return true;
        }

        return false;
    }

    private transactionNumMatches(data: ModelRow): boolean {
        const transactionNum = this.filterRow.get("transaction_num");
        if (transactionNum == null) {
            return true;
        }
        return data.get("triumphpay_transaction_num") === transactionNum;
    }

    private currencyMatches(data: ModelRow): boolean {
        const currencySettings = CurrencySettings.getSingleton();
        const multicurrencyEnabled = currencySettings.multiCurrencyEnabled();
        if (!multicurrencyEnabled) return true;

        const currency_code = this.filterRow.get("currency_type");
        if (currency_code == null) return true;

        if (currency_code == data.getCurrency("pay_amount").getCurrencyCode() || data.get("record_type") == "P") return true;

        return false;
    }

    public getFilterCurrencyType() {
        return this.filterRow.get("currency_type");
    }

    public isPayeeHourlySalary(data: ModelRow): boolean {
        return data.get("pay_method") === "H" || data.get("pay_method") === "S";
    }

    public dataPassesPayee(data: ModelRow): boolean {
        if (!data.getBoolean("non_office_emp", false)) {
            return false;
        }
        const payeeIncludeList = this.includeLists.filter((list) => list.dataField !== "back_office_reason_code");
        for (const list of payeeIncludeList) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.payeeStatusMatches(data)) {
            return false;
        }
        if (!this.payeeTypeMatches(data)) {
            return false;
        }
        if (!this.payFrequencyMatches(data)) {
            return false;
        }
        if (!this.frozenPayablesMatches(data)) {
            return false;
        }
        return true;
    }

    public dataPassesNewPayee(data: ModelRow): boolean {
        if (data.get("payee_status") !== "N") {
            return false;
        }
        const payeeIncludeList = this.includeLists.filter((list) => list.dataField !== "back_office_reason_code" && list.dataField !== "payment_method");
        for (const list of payeeIncludeList) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.payeeTypeMatches(data)) {
            return false;
        }
        if (!this.payFrequencyMatches(data)) {
            return false;
        }
        if (!this.frozenPayablesMatches(data)) {
            return false;
        }
        return true;
    }

    private getPayrollCutoffDate(filterRow: ModelRow): Date {
        const payeeType = filterRow.get("payee_type");
        const companyCutoffDate = getPayrollControlValue("comp_cutoff_date") ? new Date(getPayrollControlValue("comp_cutoff_date")) : null;
        const ownerCutoffDate = getPayrollControlValue("ownop_cutoff_date") ? new Date(getPayrollControlValue("ownop_cutoff_date")) : null;
        if (filterRow.isNull("payee_type")) {
            // Return the earlier date, or the matching date if they're equal
            return companyCutoffDate && ownerCutoffDate
            ? (companyCutoffDate <= ownerCutoffDate ? companyCutoffDate : ownerCutoffDate)
            : companyCutoffDate || ownerCutoffDate;
        }
        return payeeType === "O" ? ownerCutoffDate : companyCutoffDate;
    }
}
